import { useState, useEffect } from 'react'
import { commentApi } from '../api'
import type { ScheduledComment } from '../types'

export function CommentApproval() {
  const [comments, setComments] = useState<ScheduledComment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editedComment, setEditedComment] = useState('')

  useEffect(() => {
    loadComments()
  }, [])

  const loadComments = async () => {
    try {
      const data = await commentApi.listPending()
      setComments(data.comments)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comments')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (id: string) => {
    try {
      await commentApi.approve(id)
      await loadComments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve comment')
    }
  }

  const handleReject = async (id: string) => {
    try {
      await commentApi.reject(id)
      await loadComments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject comment')
    }
  }

  const handleStartEdit = (comment: ScheduledComment) => {
    setEditingId(comment.id)
    setEditedComment(comment.generatedComment)
  }

  const handleSaveEdit = async (id: string) => {
    try {
      await commentApi.edit(id, editedComment)
      setEditingId(null)
      setEditedComment('')
      await loadComments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to edit comment')
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditedComment('')
  }

  if (loading) return <div>Loading pending comments...</div>
  if (error) return <div style={{ color: 'red' }}>{error}</div>

  return (
    <div>
      <h2>Pending Comments</h2>
      {comments.length === 0 ? (
        <p>No pending comments. All comments are either approved or auto-posted.</p>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {comments.map((comment) => (
            <div
              key={comment.id}
              style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '20px',
                backgroundColor: '#fff',
              }}
            >
              <div style={{ marginBottom: '15px' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>
                  {comment.postTitle}
                </h3>
                <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                  Subreddit: r/{comment.postSubreddit}
                </p>
                <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                  Scheduled for: {new Date(comment.scheduledFor).toLocaleString()}
                </p>
              </div>

              <div
                style={{
                  backgroundColor: '#f8f9fa',
                  padding: '15px',
                  borderRadius: '4px',
                  marginBottom: '15px',
                }}
              >
                <strong style={{ display: 'block', marginBottom: '10px' }}>Generated Comment:</strong>
                {editingId === comment.id ? (
                  <textarea
                    value={editedComment}
                    onChange={(e) => setEditedComment(e.target.value)}
                    rows={6}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                    }}
                  />
                ) : (
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '14px' }}>
                    {comment.generatedComment}
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                {editingId === comment.id ? (
                  <>
                    <button
                      onClick={() => handleSaveEdit(comment.id)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Save & Approve
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleApprove(comment.id)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleStartEdit(comment)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleReject(comment.id)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Reject & Regenerate
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
